import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { InventoryOutputStateEnums } from 'src/app/enum/inventory-output-state-enums';
import { InventoryOutputTypeEnums } from 'src/app/enum/inventory-output-type-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { InventoryOutputDetailModel } from 'src/app/models/inventory/inventory-output-detail.model';
import { InventoryOutputModel } from 'src/app/models/inventory/inventory-output.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryOutputDetailCaptureTableModel } from 'src/app/models/tables/inventory/inventory-output-detail-capture-table.model';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { InventoryOutputDetailService } from 'src/app/services/inventory/inventory-output-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-inventory-output-detail',
  templateUrl: './inventory-output-detail.component.html',
  styleUrls: ['./inventory-output-detail.component.scss'],
  standalone: false
})
export class InventoryOutputDetailComponent implements OnInit {

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() disabledDetail: boolean = false;
  @Input() inventoryOutputModel: InventoryOutputModel = new InventoryOutputModel();
  @Input() permissionUpdatable: boolean = false;
  @Input() quantityDetails: number = 0;
  @Input() swAllowsNegativeShopBalances: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();
  @Output() hasQuantityInZero = new EventEmitter<boolean>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  dataArticle: PagedResponseModel<RentalStockModel> = new PagedResponseModel<RentalStockModel>();
  detailData: PagedResponseModel<InventoryOutputDetailCaptureTableModel> = new PagedResponseModel<InventoryOutputDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = true;
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    limitPaginator: true,
    filters: [],
    actions: [],
  });
  isFormView: boolean = false;
  inventoryOutputDetail: InventoryOutputDetailModel = new InventoryOutputDetailModel();
  limit: number = 10;
  limitArticle: number = 5;
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [];
  componentTableSelection = CustomTableSelectComponent;

  constructor(private inventoryOutputDetailService: InventoryOutputDetailService,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.getColumns();
    this.tableActions = this.getDefaultActions();

    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const inventoryOutputModel = changes["inventoryOutputModel"];
    if (inventoryOutputModel && !inventoryOutputModel.firstChange) {
      this.getFormView(false);
      this.getInventoryOutputDetails();
      this.inventoryOutputDetail = new InventoryOutputDetailModel();

      const isNewOrClosed =
        this.inventoryOutputModel.inventoryOutputId === 0 ||
        this.inventoryOutputModel.inventoryOutputStateId !== InventoryOutputStateEnums.open ||
        this.inventoryOutputModel.inventoryOutputTypeId !== InventoryOutputTypeEnums.output;

      this.disabledButton = isNewOrClosed;
      this.clearSelectionTrigger++;
    }

    const disabledDetail = changes["disabledDetail"];
    if (disabledDetail && !disabledDetail.firstChange) {
      this.tableActions = this.getDefaultActions();
    }
  }

  async createNewDetail() {
    this.inventoryOutputDetail = new InventoryOutputDetailModel();
    this.getFormView(true);
  }

  async deleteInventoryOutputDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar el detalle de la salida de inventario?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        await firstValueFrom(this.inventoryOutputDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la salida de inventario fue eliminado exitosamente.'
        );
        this.inventoryOutputDetail = new InventoryOutputDetailModel();
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        console.error('Error al eliminar el detalle de la salida de inventario.', error);
      }
    }
  }

  async handleSelectedItem(selectedItem: any) {
    if (selectedItem.length == 1) {
      const articleId = parseInt(selectedItem[0]?.articleId);
      const storeId = parseInt(selectedItem[0]?.storeId);
      const quantityPending = parseInt(selectedItem[0]?.quantityPending);
      if (this.inventoryOutputModel.swInternalOrder && (isNaN(storeId) || storeId === null)) {
        this.toastHandlerService.showToast('error', 'No se pueden agregar artículos sin existencias.');
        return;
      }
      const internalOrderDetailId = parseInt(selectedItem[0]?.internalOrderDetailId);
      const response = await firstValueFrom(this.inventoryOutputDetailService.getInventoryOutputDetailByKeys(
        this.inventoryOutputModel.inventoryOutputId, storeId, articleId
      ));
      if (response.inventoryOutputDetailId === 0) {
        this.inventoryOutputDetail = new InventoryOutputDetailModel();
        this.inventoryOutputDetail.articleId = articleId;
        this.inventoryOutputDetail.storeId = storeId;
        this.inventoryOutputDetail.internalOrderDetailId = internalOrderDetailId;
        this.inventoryOutputDetail.quantityPending = quantityPending;
      } else {
        this.inventoryOutputDetail = response;
      }
      this.getFormView(true);
    } else {
      const inventoryOutputDetails: InventoryOutputDetailModel[] = []
      selectedItem.forEach((element: { articleId: number; storeId: number; internalOrderDetailId?: number }) => {
        const newInventoryOutputDetail = new InventoryOutputDetailModel();
        newInventoryOutputDetail.inventoryOutputId = this.inventoryOutputModel.inventoryOutputId;
        newInventoryOutputDetail.inventoryOutput = this.inventoryOutputModel;
        newInventoryOutputDetail.articleId = element.articleId;
        newInventoryOutputDetail.storeId = element.storeId;
        if (this.inventoryOutputModel.swInternalOrder && element.internalOrderDetailId) {
          newInventoryOutputDetail.internalOrderDetailId = element.internalOrderDetailId;
        }
        inventoryOutputDetails.push(newInventoryOutputDetail);
      });

      if (this.inventoryOutputModel.swInternalOrder && inventoryOutputDetails.some(detail => isNaN(detail.storeId) || detail.storeId === null)) {
        this.toastHandlerService.showToast('error', 'No se pueden agregar artículos sin existencias.');
        return;
      }

      await firstValueFrom(this.inventoryOutputDetailService.createSomeDetails(inventoryOutputDetails));
      this.toastHandlerService.showToast('success', 'El detalle de la salida de inventario fue creado exitosamente', '');
      this.getInventoryOutputDetails();
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.inventoryOutputDetailService.getColumnsForCaptureTable());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsArticle() {
    try {
      this.headerArticle = await firstValueFrom(this.inventoryOutputDetailService.getColumnsForListStocksInShop());
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getColumnsArticleInternalOrder() {
    try {
      this.headerArticle = await firstValueFrom(this.inventoryOutputDetailService.getColumnsArticleInternalOrder());
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getInventoryOutputDetails() {
    try {
      this.detailData = await firstValueFrom(this.inventoryOutputDetailService.getInventoryOutputDetailsForCaptureTable(
        this.page, this.limit, this.columnSort, this.ascendant, this.inventoryOutputModel.inventoryOutputId
      ));
      this.emitQuantityDetails.emit(this.detailData.totalItems);
      this.hasQuantityInZero.emit(this.detailData.items.some(detail => detail.quantity === 0));
    } catch (error) {
      console.error('Error al buscar los detalles de la salida de inventario.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.disabledDetail) {
      return [];
    } else {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    }
  }

  async getListArticlesStocksInShop() {
    try {
      this.dataArticle = await firstValueFrom(this.inventoryOutputDetailService.getListArticlesStocksInShop(
        this.inventoryOutputModel.inventoryOutputId, this.pageArticle, this.limitArticle,
        this.columnSortArticle, this.ascendantArticle, this.filtersArticle));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getListArticlesInInternalOrder() {
    try {
      this.dataArticle = await firstValueFrom(this.inventoryOutputDetailService.getListArticlesInInternalOrder(
        this.inventoryOutputModel.inventoryOutputId, this.pageArticle, this.limitArticle,
        this.columnSortArticle, this.ascendantArticle, this.filtersArticle));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  getUpdateListDetailByDelete() {
    this.inventoryOutputDetail = new InventoryOutputDetailModel();
    this.clearSelectionTrigger++;
    this.getInventoryOutputDetails();
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async onAction(event: { action: string, item?: InventoryOutputDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      const response = await firstValueFrom(this.inventoryOutputDetailService.getInventoryOutputDetailById(event.item!.id));
      if (response) {
        this.inventoryOutputDetail = response;
      }
      this.getFormView(true);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteInventoryOutputDetail(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getInventoryOutputDetails();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    if (this.inventoryOutputModel.swInternalOrder && this.inventoryOutputModel.internalOrderId) {
      this.getListArticlesInInternalOrder();
    } else {
      this.getListArticlesStocksInShop();
    }
  }

  async openArticleSelectionModal() {
    this.filtersArticle = [];
    if (this.inventoryOutputModel.swInternalOrder && this.inventoryOutputModel.internalOrderId) {
      await this.getColumnsArticleInternalOrder();
      await this.getListArticlesInInternalOrder();
    } else {
      await this.getColumnsArticle();
      await this.getListArticlesStocksInShop();
    }
    this.updateInputs();
    this.articleSelectionModalComponent && this.articleSelectionModalComponent.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }

}
